import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { ILessonPlan, ILessonPlanCollection, ILessonPlanRequest } from '@models/lesson-plan';
import { ToastrService } from 'ngx-toastr';

import { LessonService } from '../../products/teacher/lesson.service';
import { GradeType } from '../enums/grade-type';
import { LessonPlanActionType } from '../enums/lesson-type';
import { ProductType } from '../enums/product-type';
import { MarkdownService } from '../markdown.service';

/** 
 * @deprecated
 * Still used by Legacy Class & User Stats page when Feature Flag "ClassAndUserStatsPagePortal" is disabled.
 * 
 * This can be deleted once that feature flag is deactivated.
 */
@Component({
  selector: 'zbp-lesson-planner',
  templateUrl: './lesson-planner.component.html',
  styleUrls: ['./lesson-planner.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class LessonPlannerComponent {
  dragTopicKey: string = null;

  @Input() productType: ProductType = null;
  @Input() gradeType: GradeType | string = null;
  @Input() lessonPlan: ILessonPlanCollection = null;
  @Input() isEditable: boolean = false;
  @Output() plannerUpdated: EventEmitter<ILessonPlanCollection> = new EventEmitter<ILessonPlanCollection>();


  constructor(
    private toastr: ToastrService,
    private markdown: MarkdownService,
    private lessonService: LessonService) {
  }

  get lessons(): ILessonPlan[] {
    return this.lessonPlan?.lessons || [];
  }

  renderInline(value: string): string {
    return this.markdown.renderInline(value);
  }

  getChevronClass(lessonPlanStandards): {[key: string]: boolean} {
    const isExpanded = lessonPlanStandards.isExpanded('lessonPlanStandardsPanel');
    return { 'fa-chevron-right': !isExpanded, 'fa-chevron-down': isExpanded };
  }

  printPlan(): void {
    window.print();
  }

  isDayDroppable(lesson: ILessonPlan): boolean {
    return this.isEditable
      && !!this.dragTopicKey
      && this.dragTopicKey !== lesson.topicKey
      && !lesson.isSkipDay
      && !!lesson.topicKey;
  }

  toggleSkipDay(lesson: ILessonPlan): void {
    const request: ILessonPlanRequest = {
      productType: this.productType,
      gradeType: this.gradeType,
      lessonDate: lesson.lessonDate.utc().toISOString(),
      lessonPlanAction: lesson.isSkipDay ? LessonPlanActionType.RemoveSkippedDay : LessonPlanActionType.AddSkippedDay,
    };

    if (lesson.topicKey) {
      request.topicKey = lesson.topicKey;
    }

    this.lessonService.updateTopicLessonPlan(request)
      .subscribe((res) => {
        if (res.success) {
          this.updateLessonPlan(res.response);
        } else {
          this.toastr.error(res.messages[0]);
        }
      });
  }

  lessonDragged(event: DragEvent, lesson: ILessonPlan): void {
    this.dragTopicKey = lesson.topicKey;
  }

  lessonDragCanceled(): void {
    this.dragTopicKey = null;
  }

  lessonDropped(event: DragEvent, lesson: ILessonPlan): void {
    event.preventDefault();

    const request: ILessonPlanRequest = {
      productType: this.productType,
      gradeType: this.gradeType,
      topicKey: this.dragTopicKey,
      lessonDate: lesson.lessonDate.utc().toISOString(),
      lessonPlanAction: LessonPlanActionType.AddStretchedLesson,
    };

    this.lessonService.updateTopicLessonPlan(request)
      .subscribe((res) => {
        if (res.success) {
          this.updateLessonPlan(res.response);
        } else {
          this.toastr.error(res.messages[0]);
        }
        this.dragTopicKey = null;
      });
  }

  stretchLesson(lesson: ILessonPlan, actionType: string): void {
    const lessonPlanAction = LessonPlanActionType[actionType];
    const request: ILessonPlanRequest = {
      productType: this.productType,
      gradeType: this.gradeType,
      topicKey: lesson.topicKey,
      lessonDate: lesson.lessonDate.utc().toISOString(),
      lessonPlanAction,
    };

    this.lessonService.updateTopicLessonPlan(request)
      .subscribe((res) => {
        if (res.success) {
          this.updateLessonPlan(res.response);
        } else {
          this.toastr.error(res.messages[0]);
        }
      });
  }

  private updateLessonPlan(response: ILessonPlanCollection): void {
    this.plannerUpdated.emit(response);
  }
}
